import { Injectable, signal } from '@angular/core';
import { UserRole } from '@markant-platform/shared/user/model';
import {
  AssignedRole,
  CartridgeRoleAssignmentAggregation,
  CartridgeWithSelectableRoles,
  Role,
  SelectableRole,
} from '@markant-platform/authorizer/model';
import { SelectableSuborganization, SubOrganizationData } from '@markant-platform/shared/organization/model';
import { EditRoleSuborganizationAssignments } from '@markant-platform/shared/authorizer/model';
import { RoleLimitsService } from './role-limits/role-limits.service';

@Injectable({
  providedIn: 'root',
})
export class CartridgeRoleAssignmentDataService {
  private _subOrganizations = signal<SubOrganizationData[]>([]);
  private _cartridgeRolesAssignments = signal<Record<string, UserRole[]>>({});
  private _cartridgeRoles = signal<CartridgeWithSelectableRoles[]>([]);
  private _cartridgeRolesSelected = signal<CartridgeWithSelectableRoles[]>([]);
  private roleLimitsService!: RoleLimitsService;

  cartridgeRolesAssignments = this._cartridgeRolesAssignments.asReadonly();
  cartridgeRoles = this._cartridgeRoles.asReadonly();
  selectedRoles = this._cartridgeRolesSelected.asReadonly();
  subOrganizations = this._subOrganizations.asReadonly();

  init(roleLimitsService: RoleLimitsService): void {
    this._cartridgeRolesAssignments.set({});
    this._cartridgeRoles.set([]);
    this._cartridgeRolesSelected.set([]);
    this.roleLimitsService = roleLimitsService;
  }

  resetSelectedRoles(): void {
    this._cartridgeRolesSelected.set([]);
  }

  setPreviousCartridgeRoleAssignmentsFromUserRoles(userRoles: UserRole[]): void {
    this._cartridgeRolesAssignments.set(this.groupUserRolesByLeistungIdFromUserRoles(userRoles));
  }

  setPreviousCartridgeRoleAssignmentsFromAssignedRoles(assignedRoles: AssignedRole[], groupId: string): void {
    this._cartridgeRolesAssignments.set(this.groupUserRolesByLeistungIdFromAssignedRoles(assignedRoles, groupId));
  }

  setSubOrganizations(subOrganizations: SubOrganizationData[]): void {
    this._subOrganizations.set(subOrganizations);
  }

  initializeCartridgeRoles(data: CartridgeRoleAssignmentAggregation): void {
    const cartridgeRoles: CartridgeWithSelectableRoles[] = data.rawRoles.reduce(
      (accRoles: CartridgeWithSelectableRoles[], role: Role) => {
        const index = accRoles.findIndex(accRole => accRole.id === role.cartridgeId);

        const selectableRole = {
          ...role,
          selected: this.cartridgeRolesAssignments()[role.cartridgeId]?.some(({ id }) => id === role.id) ?? false,
        };

        if (index !== -1) {
          accRoles[index].roles = [...accRoles[index].roles, selectableRole];
          return accRoles;
        }

        return [
          ...accRoles,
          {
            id: role.cartridgeId,
            name: role.localizedServiceName ?? '',
            roles: [selectableRole],
          },
        ];
      },
      []
    );

    this._cartridgeRoles.set(
      cartridgeRoles.map(cartridgeWithSelectableRole => ({
        ...cartridgeWithSelectableRole,
        limits: this.roleLimitsService.buildLimit(cartridgeWithSelectableRole, data.roleLimits),
      }))
    );
  }

  groupUserRolesByLeistungIdFromUserRoles(userRoles: UserRole[]): Record<string, UserRole[]> {
    return userRoles.reduce((accumulator: Record<string, UserRole[]>, userRole: UserRole) => {
      const { groupId, leistungId } = userRole;

      if (!groupId && leistungId) {
        accumulator = {
          ...accumulator,
          [leistungId]: [...(accumulator[leistungId] ?? []), userRole],
        };
      }

      return accumulator;
    }, {});
  }

  groupUserRolesByLeistungIdFromAssignedRoles(
    assignedRoles: AssignedRole[],
    groupId: string
  ): Record<string, UserRole[]> {
    return assignedRoles.reduce((acc: Record<string, UserRole[]>, assignedRole: AssignedRole) => {
      const {
        role: { internalName, id, cartridgeId, localizedDisplayName, localizedServiceName, localizedDescription },
        subOrganization,
        organization,
      } = assignedRole;

      const userRole: UserRole = {
        name: internalName,
        id,
        suborganizationId: subOrganization?.id,
        organizationId: organization.id,
        groupId,
        leistungId: cartridgeId,
        localizedCartridgeName: localizedServiceName ?? '',
        localizedDisplayName,
        localizedDescription,
      };

      return {
        ...acc,
        [cartridgeId]: [...(acc[cartridgeId] ?? []), userRole],
      };
    }, {});
  }

  deleteAllSelectedRoles(): void {
    this._cartridgeRolesSelected.set([]);
  }

  freeAllVariantRoleLimitSlots(): void {
    this._cartridgeRoles.update(cartridgeRoles =>
      cartridgeRoles.map(cartRole => {
        if (
          cartRole.limits &&
          cartRole.roles.some(({ selected }) => selected) &&
          this._cartridgeRolesSelected().some(({ id }) => id === cartRole.id)
        ) {
          return {
            ...cartRole,
            limits: this.roleLimitsService.incrementLimits(cartRole.limits),
          };
        }

        return cartRole;
      })
    );
  }

  unselectCartridgeRoles(): void {
    this._cartridgeRoles.update(value =>
      value.map((cartridge: CartridgeWithSelectableRoles) => ({
        ...cartridge,
        roles: cartridge.roles.map((role: SelectableRole) => ({ ...role, selected: false, suborganizationData: [] })),
      }))
    );
  }

  deleteSelectedRole(role: SelectableRole): void {
    const { cartridgeId, id } = role;

    this._cartridgeRolesSelected.update(cartridgeWithRoles => {
      const index = cartridgeWithRoles.findIndex(({ id }) => id === cartridgeId);

      if (index === -1) {
        return cartridgeWithRoles;
      }

      return cartridgeWithRoles.reduce(
        (result: CartridgeWithSelectableRoles[], curr: CartridgeWithSelectableRoles, currIndex: number) => {
          if (currIndex === index) {
            const rolesUpdated: SelectableRole[] = curr.roles.filter(role => role.id !== id);

            if (rolesUpdated.length) {
              return [...result, { ...curr, roles: rolesUpdated }];
            }

            return result;
          }

          return [...result, curr];
        },
        []
      );
    });
  }

  toggleRoleSelection(roleId: string, isSelected: boolean): void {
    this._cartridgeRoles.update(cartridgeRoles => {
      return cartridgeRoles.map(item => {
        const hasRole = item.roles.some(({ id }) => id === roleId);

        if (hasRole) {
          return {
            ...item,
            roles: item.roles.map(role => ({
              ...role,
              ...(role.id === roleId && { selected: isSelected }),
            })),
          };
        }

        return item;
      });
    });
  }

  decreaseSlots(variantId: string): void {
    this._cartridgeRoles.update(cartridgeRoles =>
      cartridgeRoles.map(item => {
        if (
          item.id === variantId &&
          item.limits?.availableSlots &&
          item.roles.filter(({ selected }) => selected).length <= 1
        ) {
          return {
            ...item,
            limits: this.roleLimitsService.decrementLimits(item.limits),
          };
        }

        return item;
      })
    );
  }

  increaseSlots(variantId: string): void {
    this._cartridgeRoles.update(cartridgeRoles =>
      cartridgeRoles.map(item => {
        if (
          item.id === variantId &&
          item.limits?.availableSlots !== undefined &&
          !item.roles.some(({ selected }) => selected)
        ) {
          return {
            ...item,
            limits: this.roleLimitsService.incrementLimits(item.limits),
          };
        }
        return item;
      })
    );
  }

  addRole(newRole: Role): void {
    const { id, cartridgeId, localizedServiceName } = newRole;

    this._cartridgeRolesSelected.update(selectedCartridgeRole => {
      const cartridgeRole = selectedCartridgeRole.find(c => c.id === cartridgeId);

      if (!cartridgeRole) {
        return [...selectedCartridgeRole, { id: cartridgeId, name: localizedServiceName ?? '', roles: [newRole] }];
      }

      if (cartridgeRole.roles.some(role => role.id === id)) {
        return selectedCartridgeRole;
      }

      return selectedCartridgeRole.map(selectedCartRole => ({
        ...selectedCartRole,
        ...(selectedCartRole.id === cartridgeId && { roles: [...selectedCartRole.roles, newRole] }),
      }));
    });
  }

  updateSubOrganizations(event: EditRoleSuborganizationAssignments): void {
    this._cartridgeRolesSelected.update(roles =>
      roles.map(cartridgeRoleSelected => ({
        ...cartridgeRoleSelected,
        ...(cartridgeRoleSelected.id === event.role.cartridgeId && {
          roles: cartridgeRoleSelected.roles.map(role => ({
            ...role,
            ...(role.id === event.role.id && { suborganizationData: event.suborganizations ?? [] }),
          })),
        }),
      }))
    );
  }

  initializeExistingRoles(): void {
    if (!Object.keys(this._cartridgeRolesAssignments()).length) {
      return;
    }

    this._cartridgeRolesSelected.set(this.getCartridgeSelectedRoles());
  }

  private getCartridgeSelectedRoles(): CartridgeWithSelectableRoles[] {
    return this._cartridgeRoles().reduce<CartridgeWithSelectableRoles[]>((selectedRoles, cartridgeRole) => {
      const { id, name, limits } = cartridgeRole;
      const cartridgeId = Object.keys(this._cartridgeRolesAssignments()).find(cartridgeId => cartridgeId === id);

      if (cartridgeId) {
        const roles = this.getRolesWithSuborganizations(cartridgeRole, cartridgeId);

        if (roles.length) {
          return [
            ...selectedRoles,
            {
              id,
              name,
              roles,
              limits,
            },
          ];
        }
      }
      return selectedRoles;
    }, []);
  }

  private getRolesWithSuborganizations(
    cartridgeRole: CartridgeWithSelectableRoles,
    cartridgeId: string
  ): SelectableRole[] {
    return cartridgeRole.roles.reduce<SelectableRole[]>((result, role) => {
      const matchingRoles = this.getAssignedRoles(cartridgeId, role.id);

      if (matchingRoles.length) {
        return [
          ...result,
          { ...role, selected: true, suborganizationData: this.getSuborganizationsForRole(matchingRoles) },
        ];
      }

      return result;
    }, []);
  }

  private getAssignedRoles(cartridgeId: string, roleId: string): UserRole[] {
    return this._cartridgeRolesAssignments()[cartridgeId]?.filter(({ id }) => id === roleId) ?? [];
  }

  private getSuborganizationsForRole(matchingRoles: UserRole[]): SelectableSuborganization[] {
    return matchingRoles
      .filter(role => role.suborganizationId)
      .map(role => {
        const suborganization = this._subOrganizations().find(sub => sub.id === role.suborganizationId);

        return {
          id: role.suborganizationId ?? '',
          name: suborganization ? suborganization.name : '-',
          selected: true,
        };
      });
  }
}
